/**
 * Declarative registry of every fractal this app renders. Each entry names
 * which compiled shader implements it and which *extra* uniform fields (past
 * the six shared view fields — width, height, center_re, center_im, scale,
 * max_iter) that shader's `Params` struct expects, in declaration order.
 *
 * This is the single source of truth both `render.mjs` (packs the extra
 * params into the uniform buffer) and `app.mjs` (builds the extra-param
 * controls in the sidebar) read from — adding a fractal means adding one
 * entry here plus one .wgsl file, not touching the renderer or the UI code.
 *
 * `precision: "f64"` entries pack center_re/center_im/scale as (hi, lo)
 * pairs instead (see util/f64.mjs); `hidden: true` keeps them out of the
 * dropdown, reached only via another entry's `f64Variant` precision toggle.
 *
 * @module wgfractals/fractals/formulas
 */

export const FORMULAS = {
  mandelbrot: {
    label: "Mandelbrot",
    shader: "mandelbrot",
    extraParams: [],
    defaultView: { centerRe: -0.5, centerIm: 0, scale: 1 / 200 },
    defaultMaxIter: 200,
    // f64-precision FORMULAS entry, swapped in by activeShaderFormula() when toggled on.
    f64Variant: "mandelbrotf64",
  },
  julia: {
    label: "Julia",
    shader: "julia",
    extraParams: [
      { name: "cRe", type: "f32", label: "c (real)", default: -0.7, min: -2, max: 2, step: 0.001 },
      { name: "cIm", type: "f32", label: "c (imag)", default: 0.27, min: -2, max: 2, step: 0.001 },
    ],
    defaultView: { centerRe: 0, centerIm: 0, scale: 1 / 200 },
    defaultMaxIter: 200,
  },
  burningship: {
    label: "Burning Ship",
    shader: "burningship",
    extraParams: [],
    defaultView: { centerRe: -0.4, centerIm: -0.6, scale: 1 / 200 },
    defaultMaxIter: 200,
  },
  tricorn: {
    label: "Tricorn",
    shader: "tricorn",
    extraParams: [],
    defaultView: { centerRe: 0, centerIm: 0, scale: 1 / 200 },
    defaultMaxIter: 200,
  },
  multibrot: {
    label: "Multibrot",
    shader: "multibrot",
    extraParams: [
      { name: "degree", type: "u32", label: "degree (n)", default: 3, min: 2, max: 8, step: 1 },
    ],
    defaultView: { centerRe: 0, centerIm: 0, scale: 1 / 200 },
    defaultMaxIter: 200,
  },
  newton: {
    label: "Newton (z³ − 1)",
    shader: "newton",
    extraParams: [],
    defaultView: { centerRe: 0, centerIm: 0, scale: 1 / 200 },
    defaultMaxIter: 40,
  },

  // Mandelbrot's f64 variant — hidden, reached only via the precision toggle.
  mandelbrotf64: {
    label: "Mandelbrot (f64)",
    shader: "f64/mandelbrot",
    extraParams: [],
    defaultView: { centerRe: -0.5, centerIm: 0, scale: 1 / 200 },
    defaultMaxIter: 200,
    precision: "f64",
    hidden: true,
  },
};
